/**
 * Situation financière d'une classe, élève par élève.
 *
 * Reconstruit, depuis les élèves et leurs paiements, le tableau que l'école
 * veut lire d'un coup d'œil : une colonne par paiement appliqué à la classe,
 * une ligne par élève, et la situation à l'intersection.
 *
 * Les colonnes sont déduites des paiements que portent RÉELLEMENT les élèves,
 * et non de la configuration de la classe. Les deux devraient coïncider, mais
 * en cas d'écart c'est la donnée de l'élève qui compte : une colonne sans
 * aucune ligne n'apprend rien, et un paiement sans colonne disparaîtrait du
 * total sans qu'on le voie.
 *
 * Les montants versés passent par `paidOnPayment`, qui sait déjà lire les
 * trois formes que prend un versement selon l'endroit d'où vient la donnée.
 */
import { isPaymentSettled, paidOnPayment } from "@/utils/receiptModel";
import { familyNameOf, givenNameOf, sortByFamilyName } from "@/utils/studentName";

/** Situation d'un élève sur un paiement donné. */
export const SITUATION = {
  /** Intégralement réglé. */
  SETTLED: "settled",
  /** Un acompte est entré, il reste à payer. */
  ADVANCE: "advance",
  /** Dû, rien d'encaissé. */
  PENDING: "pending",
  /** Ce paiement n'est pas appliqué à cet élève. */
  NONE: "none",
};

function amountOf(payment) {
  const value = Number((payment || {}).amount);
  return Number.isFinite(value) ? value : 0;
}

/**
 * Rang d'une tranche de scolarité.
 *
 * L'API ne renvoie pas toujours le numéro : sur plusieurs classes, les
 * tranches arrivent sans `number`. Le rang est alors la position parmi les
 * tranches de l'élève, l'API les renvoyant dans l'ordre des échéances. C'est
 * le repli que `resolveReceipt` applique déjà pour intituler un reçu.
 *
 * Sans lui, toutes les tranches partageaient la clé « tuition: » et se
 * fondaient en UNE colonne, intitulée « Tranche » sans numéro.
 *
 * @param {number} position rang de la tranche chez l'élève, à partir de 1
 */
export function tuitionRankOf(payment, position) {
  const candidates = [
    (payment || {}).number,
    (payment || {}).installment_number,
    (payment || {}).rank,
  ];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return position;
}

/** Nom d'un autre paiement, qui sert aussi de clé de colonne. */
export function otherNameOf(payment, position) {
  const name = String(
    (payment || {}).label || (payment || {}).description || ""
  ).trim();
  // Sans libellé, on retombe sur la position : deux paiements anonymes ne
  // doivent pas se confondre en une seule colonne.
  return name || `#${position}`;
}

/**
 * Clé de colonne d'un paiement.
 *
 * Une tranche est identifiée par son rang, un autre paiement par son nom :
 * ce sont les seules données qui se répètent à l'identique d'un élève à
 * l'autre. L'identifiant du paiement, lui, est propre à chaque élève —
 * s'en servir donnerait une colonne par élève.
 *
 * @param {number} position rang du paiement dans sa famille, à partir de 1
 */
export function columnKeyOf(payment, position = 1) {
  if (!payment) return "";
  if (payment.is_other_payment) {
    return `other:${otherNameOf(payment, position)}`;
  }
  return `tuition:${tuitionRankOf(payment, position)}`;
}

/** Libellé de colonne, dans la langue de l'interface. */
function columnLabelOf(payment, position, locale) {
  if (payment.is_other_payment) {
    return otherNameOf(payment, position);
  }
  const word = locale === "en" ? "Instalment" : "Tranche";
  return `${word} ${tuitionRankOf(payment, position)}`;
}

/**
 * Parcourt les paiements d'un élève en tenant deux compteurs séparés.
 *
 * Scolarité et autres paiements sont numérotés chacun de leur côté : la
 * position d'un autre paiement ne doit pas dépendre du nombre de tranches
 * qui le précèdent dans le tableau.
 */
function eachPayment(student, visit) {
  let tuitionSeen = 0;
  let otherSeen = 0;
  ((student || {}).payments || []).forEach((payment) => {
    if (!payment) return;
    const position = payment.is_other_payment ? ++otherSeen : ++tuitionSeen;
    visit(payment, position);
  });
}

/** Situation d'un élève sur un paiement. */
export function situationOf(payment) {
  if (!payment) return SITUATION.NONE;
  if (isPaymentSettled(payment)) return SITUATION.SETTLED;
  return paidOnPayment(payment) > 0 ? SITUATION.ADVANCE : SITUATION.PENDING;
}

/**
 * Construit le tableau de la classe.
 *
 * @param {Array}  students liste d'élèves, chacun avec son tableau `payments`
 * @param {string} locale   langue de l'interface
 * @returns {{columns: Array, rows: Array, totals: object}}
 */
export function buildClassPaymentOverview(students, locale = "fr") {
  const list = (students || []).filter(Boolean);

  /*
   * Colonnes, dans l'ordre où on les lit : la scolarité d'abord, par numéro
   * de tranche, puis les autres paiements par ordre d'apparition. C'est
   * l'ordre du menu « Sélectionner une tranche », donc celui que l'école a
   * déjà en tête.
   */
  const seen = new Map();
  list.forEach((student) => {
    eachPayment(student, (payment, position) => {
      const key = columnKeyOf(payment, position);
      if (seen.has(key)) return;
      seen.set(key, {
        key,
        label: columnLabelOf(payment, position, locale),
        amount: amountOf(payment),
        kind: payment.is_other_payment ? "other" : "tuition",
        rank: payment.is_other_payment ? null : tuitionRankOf(payment, position),
      });
    });
  });

  const columns = [...seen.values()].sort((left, right) => {
    if (left.kind !== right.kind) return left.kind === "tuition" ? -1 : 1;
    if (left.kind === "tuition") {
      const a = Number.isFinite(left.rank) ? left.rank : Number.MAX_SAFE_INTEGER;
      const b = Number.isFinite(right.rank) ? right.rank : Number.MAX_SAFE_INTEGER;
      return a - b;
    }
    return 0;
  });

  /* Lignes, classées par nom de famille comme la liste de la classe. */
  const rows = sortByFamilyName(list, locale).map((student) => {
    const cells = {};
    let due = 0;
    let paid = 0;

    eachPayment(student, (payment, position) => {
      const paidHere = paidOnPayment(payment);
      const dueHere = amountOf(payment);
      due += dueHere;
      paid += paidHere;
      cells[columnKeyOf(payment, position)] = {
        situation: situationOf(payment),
        paid: paidHere,
        due: dueHere,
        remaining: Math.max(dueHere - paidHere, 0),
      };
    });

    return {
      student,
      registration_number: student.registration_number || "",
      family_name: familyNameOf(student),
      given_name: givenNameOf(student),
      cells,
      due,
      paid,
      /*
       * Reste à payer de l'élève : la différence entre ses totaux, et non la
       * somme des écarts échéance par échéance. Les deux ne donnent le même
       * résultat que si aucune tranche n'est sur-payée — auquel cas le
       * trop-perçu vient compenser un manque ailleurs. C'est le comportement
       * voulu : l'école raisonne sur ce que la famille doit encore en tout.
       *
       * Le reste de chaque tranche est quand même calculé, dans `cells`, pour
       * qui voudrait l'écart échéance par échéance.
       */
      remaining: Math.max(due - paid, 0),
    };
  });

  const totals = rows.reduce(
    (sum, row) => ({
      due: sum.due + row.due,
      paid: sum.paid + row.paid,
      remaining: sum.remaining + row.remaining,
    }),
    { due: 0, paid: 0, remaining: 0 }
  );

  return { columns, rows, totals };
}
